import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Users, Building2, Settings,
  LogOut, ChevronRight, Shield, BarChart3, X, DollarSign, Globe, ClipboardList, SlidersHorizontal, BookText, BookOpen, Server, User, Sparkles, FileSignature, TrendingUp
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import apiClient from '../../services/api';

const C4P_LOGO = 'https://customer-assets.emergentagent.com/job_c4p-quoting-hub/artifacts/dnrg8j6i_C4P%20Transparent.png';

const NavItem = ({ to, icon: Icon, label, onClick, badge }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : ''}`}
    data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="relative flex-shrink-0">
      <Icon size={15} strokeWidth={2} />
      {badge > 0 && (
        <span
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
          style={{ background: '#f59e0b' }}
          aria-label={`${badge} unreviewed`}
        />
      )}
    </div>
    <span>{label}</span>
  </NavLink>
);

const NavSection = ({ label }) => <div className="sidebar-nav-section">{label}</div>;

export function Sidebar({ onClose }) {
  const { user, logout, isAdmin, isManager } = useAuth();
  const isSales = user?.role === 'SALES';
  const isAccounts = user?.role === 'ACCOUNTS';
  const navigate = useNavigate();
  const roleLabel = { SUPER_ADMIN: 'Super Admin', MANAGER: 'Manager', SALES: 'PSM', ACCOUNTS: 'Accounts' }[user?.role] || 'User';
  const [unreviewedCrawls, setUnreviewedCrawls] = useState(0);

  // Poll unreviewed crawl count every 60s (admin/manager only)
  useEffect(() => {
    if (!isAdmin && !isManager) return;
    const fetch = async () => {
      try {
        const { data } = await apiClient.get('/admin/crawl/unreviewed-count');
        setUnreviewedCrawls(data.count || 0);
      } catch { /* ignore */ }
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => clearInterval(interval);
  }, [isAdmin, isManager]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('Signed out');
  };

  return (
    <div
      className="flex flex-col h-full w-[272px]"
      style={{ background: 'hsl(var(--sidebar-bg))', color: 'hsl(var(--sidebar-fg))' }}
    >
      {/* Logo / Header */}
      <div
        className="flex items-center justify-between px-4 py-4"
        style={{ borderBottom: '1px solid hsl(var(--sidebar-muted))' }}
      >
        <div className="flex items-center gap-3">
          <img
            src={C4P_LOGO}
            alt="Cloud4Partners"
            className="h-9 w-auto object-contain"
            onError={e => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          {/* Fallback badge if image fails */}
          <div
            className="hidden w-9 h-9 rounded-xl items-center justify-center font-bold text-xs flex-shrink-0"
            style={{ background: 'hsl(var(--c4p-brand-gold))', color: '#1a1a1a' }}
          >
            C4P
          </div>
          <div>
            <div
              className="text-sm font-bold leading-tight"
              style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--sidebar-fg))' }}
            >
              Quoting Portal
            </div>
            <div className="text-xs" style={{ color: 'hsl(var(--sidebar-fg) / 0.45)' }}>
              Internal Sales Tool
            </div>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-md"
            style={{ color: 'hsl(var(--sidebar-fg) / 0.4)' }}
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
        <NavSection label="Main" />
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" onClick={onClose} />
        <NavItem to="/quotes"    icon={FileText}        label="Quotes"     onClick={onClose} />
        <NavItem to="/contracts" icon={FileSignature}   label="Contracts"  onClick={onClose} />
        <NavItem to="/customers" icon={Building2}       label="Partners"  onClick={onClose} />
        <NavItem to="/tenants"   icon={Users}           label="Tenants"   onClick={onClose} />
        <NavItem to="/resources" icon={BookOpen}        label="Resources" onClick={onClose} />

        {isManager && (
          <>
            <div className="my-2">
              <Separator style={{ background: 'hsl(var(--sidebar-muted))' }} />
            </div>
            <NavSection label="Management" />
            <NavItem to="/reports" icon={BarChart3} label="Reports" onClick={onClose} />
          </>
        )}

        {/* PSM Tools - visible to PSMs only (Managers/Admins already see these via Admin section) */}
        {isSales && (
          <>
            <div className="my-2">
              <Separator style={{ background: 'hsl(var(--sidebar-muted))' }} />
            </div>
            <NavSection label="Tools" />
            <NavItem to="/reports"                       icon={BarChart3}          label="Reports"             onClick={onClose} />
          </>
        )}

        {/* Accounts role — read-only view of everything except admin */}
        {isAccounts && (
          <>
            <div className="my-2">
              <Separator style={{ background: 'hsl(var(--sidebar-muted))' }} />
            </div>
            <NavSection label="Reporting" />
            <NavItem to="/reports" icon={BarChart3} label="Reports" onClick={onClose} />
          </>
        )}

        {isAdmin && (
          <>
            <div className="my-2">
              <Separator style={{ background: 'hsl(var(--sidebar-muted))' }} />
            </div>
            <NavSection label="Administration" />
            <NavItem to="/admin/users"      icon={Users}        label="Users"      onClick={onClose} />
            <NavItem to="/admin/pricing"    icon={Shield}       label="Pricing"    onClick={onClose} />
            <NavItem to="/admin/currencies" icon={DollarSign}   label="Currencies" onClick={onClose} />
            <NavItem to="/admin/regions"       icon={Globe}               label="Regions"       onClick={onClose} />
            <NavItem to="/admin/quote-options"        icon={SlidersHorizontal}   label="Quote Options"        onClick={onClose} />
            <NavItem to="/admin/resources"             icon={BookOpen}            label="Resources"            onClick={onClose} />
            <NavItem to="/admin/resource-generator"    icon={Sparkles}            label="Resource Generator"   onClick={onClose} />
            <NavItem to="/admin/crawl-manager"         icon={Globe}               label="Website Sync"         onClick={onClose} badge={unreviewedCrawls} />
            <NavItem to="/admin/service-description"  icon={BookText}            label="Service Description"  onClick={onClose} />
            <NavItem to="/admin/settings"   icon={Settings}     label="Settings"   onClick={onClose} />
            <NavItem to="/admin/audit"      icon={ClipboardList} label="Audit Log"  onClick={onClose} />
            <NavItem to="/admin/fx-rates"   icon={TrendingUp}    label="FX Rates"   onClick={onClose} />
          </>
        )}
      </nav>

      {/* User footer */}
      <div
        className="px-3 pb-3 pt-3"
        style={{ borderTop: '1px solid hsl(var(--sidebar-muted))' }}
      >
        <div
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1.5"
          style={{ background: 'hsl(var(--sidebar-muted))' }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: 'hsl(var(--c4p-brand-blue))', color: 'hsl(218 52% 12%)' }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="text-xs font-semibold truncate"
              style={{ color: 'hsl(var(--sidebar-fg))' }}
            >
              {user?.name}
            </div>
            <div className="text-xs truncate" style={{ color: 'hsl(var(--sidebar-fg) / 0.45)' }}>
              {roleLabel}
            </div>
          </div>
          <ChevronRight size={13} style={{ color: 'hsl(var(--sidebar-fg) / 0.3)', flexShrink: 0 }} />
        </div>

        <NavItem to="/profile" icon={User} label="My Profile" onClick={onClose} />

        <button
          onClick={handleLogout}
          className="sidebar-nav-item w-full"
          style={{ background: 'transparent', border: 'none' }}
          data-testid="sidebar-logout-button"
        >
          <LogOut size={14} strokeWidth={2} />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
}
